import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Download,
  Layers,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Stamp,
  Wand2,
} from "lucide-react";
import { FORMAT_CATALOG, formatKey } from "./formats";
import { ImageModal } from "./ImageModal";
import { ImageEditModal } from "./ImageEditModal";
import LogoSwapPage from "./LogoSwapPage";
import { TerminalPanel } from "./TerminalPanel";
import { API_URL, BACKEND_URL } from "./backendUrl";

const API = API_URL;

const FormatHoverPreview = ({ preview, imageUrl }) => {
  if (!preview || !imageUrl) return null;
  const MAX = 160;
  const ratio = preview.width / preview.height;
  let w, h;
  if (ratio >= 1) {
    w = MAX;
    h = Math.max(Math.round(MAX / ratio), 16);
  } else {
    h = MAX;
    w = Math.max(Math.round(MAX * ratio), 16);
  }
  let top = preview.top - h - 40;
  if (top < 8) top = preview.bottom + 10;
  const left = Math.min(Math.max(preview.cx - w / 2, 8), window.innerWidth - w - 16);
  return (
    <div className="fixed z-[60] pointer-events-none drop-shadow-2xl" style={{ left, top }} data-testid="format-hover-preview">
      <div style={{ width: w, height: h }} className="overflow-hidden rounded-md border-2 border-[#f58207] bg-black">
        <img src={imageUrl} alt="aperçu recadrage" className="w-full h-full object-cover" />
      </div>
      <div className="mt-1 bg-slate-900 text-white text-[10px] font-mono px-2 py-0.5 rounded inline-block">
        {preview.name} · {preview.width}×{preview.height}
      </div>
    </div>
  );
};

export default function DeclinationsPage({ job, onBack, onLogout }) {
  const [currentJob, setCurrentJob] = useState(job);
  const [selectedImages, setSelectedImages] = useState(new Set([0, ...job.images.map(i => i.index)]));
  const [selectedFormats, setSelectedFormats] = useState(new Set());
  const [customWidth, setCustomWidth] = useState("");
  const [customHeight, setCustomHeight] = useState("");
  const [customName, setCustomName] = useState("");
  const [customFormats, setCustomFormats] = useState([]);
  const [decl, setDecl] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [hoverPreview, setHoverPreview] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [showLogoSwap, setShowLogoSwap] = useState(false);
  const [rerunningIndex, setRerunningIndex] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    setCurrentJob(job);
  }, [job]);

  const originalImage = { index: 0, url: currentJob.source_url, format_name: "Image originale", category: "SOURCE", width: 0, height: 0, source_index: 0, isOriginal: true };
  const previewImage = currentJob.images.find(i => selectedImages.has(i.index)) || (selectedImages.has(0) ? originalImage : currentJob.images[0]);

  const showPreview = (e, item) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHoverPreview({ ...item, cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };
  const hidePreview = () => setHoverPreview(null);

  const isRunning = launching || decl?.status === "pending" || decl?.status === "running";

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = useCallback((id) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/declinations/${id}`);
        setDecl(res.data);
        if (["completed", "partial", "failed"].includes(res.data.status)) {
          stopPolling();
          if (res.data.status === "completed") toast.success("Déclinaisons générées !");
          else if (res.data.status === "partial") toast.warning("Déclinaisons partielles");
          else toast.error("Échec des déclinaisons");
        }
      } catch { /* keep polling */ }
    }, 2000);
  }, []);

  useEffect(() => stopPolling, []);

  const toggleImage = (idx) => {
    const s = new Set(selectedImages);
    s.has(idx) ? s.delete(idx) : s.add(idx);
    setSelectedImages(s);
  };
  const toggleAllImages = () => {
    const allIndices = [0, ...currentJob.images.map(i => i.index)];
    setSelectedImages(selectedImages.size === allIndices.length ? new Set() : new Set(allIndices));
  };

  const toggleFormat = (key) => {
    const s = new Set(selectedFormats);
    s.has(key) ? s.delete(key) : s.add(key);
    setSelectedFormats(s);
  };
  const toggleAllInCategory = (cat) => {
    const allKeys = cat.items.map(it => formatKey(cat.category, it));
    const s = new Set(selectedFormats);
    const allChecked = allKeys.every(k => s.has(k));
    allKeys.forEach(k => allChecked ? s.delete(k) : s.add(k));
    setSelectedFormats(s);
  };

  const hasPendingCustomDimensions = () => String(customWidth).trim() || String(customHeight).trim();
  const sameCustomFormat = (a, b) => a.name === b.name && a.width === b.width && a.height === b.height;
  const parseCustomFormatInput = () => {
    const w = parseInt(customWidth, 10);
    const h = parseInt(customHeight, 10);
    if (!w || !h || w <= 0 || h <= 0 || w > 8192 || h > 8192) {
      toast.error("Dimensions invalides (1-8192 px)");
      return null;
    }
    const name = customName.trim() || `${w}x${h}`;
    return { name, width: w, height: h };
  };

  const totalAvailable = FORMAT_CATALOG.reduce((a, c) => a + c.items.length, 0) + customFormats.length;
  const totalSelected = selectedFormats.size;

  const toggleSelectAll = () => {
    if (totalSelected === totalAvailable) {
      setSelectedFormats(new Set());
    } else {
      const s = new Set();
      FORMAT_CATALOG.forEach(c => c.items.forEach(it => s.add(formatKey(c.category, it))));
      customFormats.forEach(cf => s.add(formatKey("CUSTOM", cf)));
      setSelectedFormats(s);
    }
  };

  const addCustomFormat = () => {
    const item = parseCustomFormatInput();
    if (!item) return null;
    if (customFormats.some(f => sameCustomFormat(f, item))) {
      toast.error("Format déjà ajouté");
      return null;
    }
    setCustomFormats([...customFormats, item]);
    const s = new Set(selectedFormats);
    s.add(formatKey("CUSTOM", item));
    setSelectedFormats(s);
    setCustomWidth(""); setCustomHeight(""); setCustomName("");
    toast.success(`Format ${item.name} ajouté`);
    return item;
  };

  const removeCustomFormat = (item) => {
    const key = formatKey("CUSTOM", item);
    setCustomFormats(customFormats.filter(f => !(f.name === item.name && f.width === item.width && f.height === item.height)));
    const s = new Set(selectedFormats); s.delete(key); setSelectedFormats(s);
  };

  const launch = async () => {
    if (selectedImages.size === 0) return toast.error("Sélectionnez au moins une créa");

    let launchCustomFormats = customFormats;
    const launchSelectedFormats = new Set(selectedFormats);
    if (hasPendingCustomDimensions()) {
      const pendingCustomFormat = parseCustomFormatInput();
      if (!pendingCustomFormat) return;
      const existingCustomFormat = launchCustomFormats.find(f => sameCustomFormat(f, pendingCustomFormat));
      const item = existingCustomFormat || pendingCustomFormat;
      if (!existingCustomFormat) {
        launchCustomFormats = [...launchCustomFormats, pendingCustomFormat];
        setCustomFormats(launchCustomFormats);
        setCustomWidth(""); setCustomHeight(""); setCustomName("");
        toast.success(`Format ${pendingCustomFormat.name} ajouté`);
      }
      launchSelectedFormats.add(formatKey("CUSTOM", item));
      setSelectedFormats(new Set(launchSelectedFormats));
    }

    if (launchSelectedFormats.size === 0) return toast.error("Sélectionnez au moins un format");

    const formats = [];
    FORMAT_CATALOG.forEach(c => c.items.forEach(it => {
      if (launchSelectedFormats.has(formatKey(c.category, it))) {
        formats.push({ name: it.name, width: it.width, height: it.height, category: c.category });
      }
    }));
    launchCustomFormats.forEach(it => {
      if (launchSelectedFormats.has(formatKey("CUSTOM", it))) {
        formats.push({ name: it.name, width: it.width, height: it.height, category: "CUSTOM" });
      }
    });

    setLaunching(true);
    setDecl(null);
    try {
      const res = await axios.post(`${API}/declinations`, {
        job_id: currentJob.id,
        image_indices: Array.from(selectedImages),
        formats,
      });
      setDecl(res.data);
      startPolling(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de lancement");
    } finally { setLaunching(false); }
  };

  const downloadZip = async () => {
    if (!decl) return;
    try {
      const res = await fetch(`${API}/declinations/${decl.id}/zip`, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `declinaisons_${decl.id.slice(0, 8)}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Échec du téléchargement"); }
  };

  const rerunDeclination = async (resultIndex) => {
    if (!decl || rerunningIndex !== null) return;
    setRerunningIndex(resultIndex);
    try {
      const res = await axios.post(`${API}/declinations/${decl.id}/rerun`, {
        result_index: resultIndex,
      });
      setDecl(res.data);
      toast.success("Déclinaison relancée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de relancer cette déclinaison");
    } finally {
      setRerunningIndex(null);
    }
  };

  const downloadReady = decl && (decl.status === "completed" || decl.status === "partial") && (decl.results?.length || 0) > 0;
  const progress = decl ? `${decl.completed || 0}/${decl.total || 0}` : "";

  const handleImageEdited = (updatedJob) => {
    if (updatedJob) {
      setCurrentJob(updatedJob);
    }
  };

  const submitImageEdit = async ({ jobId, prompt, indices, sourceImage }) => {
    if (!jobId || !prompt?.trim()) return null;
    const targetIndices = Array.from(new Set(indices || [])).sort((a, b) => a - b);
    if (targetIndices.length === 0) return null;
    let livePollRef = null;
    const startLiveJobRefresh = () => {
      if (livePollRef) return;
      livePollRef = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/jobs/${jobId}`);
          setCurrentJob(res.data);
        } catch {
          /* keep refreshing */
        }
      }, 1200);
    };
    const stopLiveJobRefresh = () => {
      if (livePollRef) {
        clearInterval(livePollRef);
        livePollRef = null;
      }
    };

    startLiveJobRefresh();
    try {
      if (sourceImage?.declinationId !== undefined && typeof sourceImage?.declinationResultIndex === "number") {
        let updatedDecl = null;
        for (const resultIndex of targetIndices) {
          const res = await axios.post(
            `${API}/declinations/${sourceImage.declinationId}/results/${resultIndex}/edit`,
            { prompt: prompt.trim() },
          );
          updatedDecl = res.data.declination;
          setDecl(updatedDecl);
        }
        return updatedDecl;
      }

      if (targetIndices.length === 1) {
        const res = await axios.post(`${API}/jobs/${jobId}/images/${targetIndices[0]}/edit`, { prompt: prompt.trim() });
        setCurrentJob(res.data.job);
        return res.data.job;
      }

      const res = await axios.post(`${API}/jobs/${jobId}/images/edit-batch`, {
        prompt: prompt.trim(),
        indices: targetIndices,
      });
      setCurrentJob(res.data.job);
      return res.data.job;
    } finally {
      stopLiveJobRefresh();
    }
  };

  if (showLogoSwap && decl) {
    return <LogoSwapPage decl={decl} onBack={() => setShowLogoSwap(false)} />;
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-body text-slate-700">
      {editingImage && currentJob?.id && (
        <ImageEditModal
          jobId={currentJob.id}
          image={editingImage}
          quickPrompts={[
            "Rends le visuel plus propre et plus lisible.",
            "Renforce légèrement le contraste et garde la composition.",
            "L'image n'est pas nette, elle est trop pixelisée.",
          ]}
          showTargetsAccordion
          availableImages={
            editingImage?.declinationId !== undefined
              ? (decl?.results || []).map((result, idx) => ({
                  index: idx,
                  url: result.url,
                  label: `${result.format_name} · ${result.width}×${result.height}`,
                  filename: result.filename,
                  isOriginal: (result.source_index ?? idx) === 0,
                  declinationId: decl.id,
                  declinationResultIndex: idx,
                  sourceIndex: result.source_index ?? idx,
                  category: result.category,
                  format_name: result.format_name,
                  width: result.width,
                  height: result.height,
                }))
              : [
                  ...(currentJob?.source_url
                    ? [{ index: 0, url: currentJob.source_url, isOriginal: true, label: "Image originale" }]
                    : []),
                  ...(currentJob?.images || []),
                ]
          }
          onClose={() => setEditingImage(null)}
          onSubmitEdit={submitImageEdit}
        />
      )}
      {modalImage && <ImageModal image={modalImage} onClose={() => setModalImage(null)} />}
      <FormatHoverPreview
        preview={hoverPreview}
        imageUrl={previewImage ? `${BACKEND_URL}${previewImage.url}` : null}
      />
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-to-generator-button"
            onClick={onBack}
            className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#f58207] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Retour aux variations
          </button>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg overflow-hidden bg-white border border-slate-200 shadow-sm flex items-center justify-center">
              <img src="/logo-gm.png" alt="" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-base font-bold tracking-tight text-slate-900 font-heading">Déclinaisons multi-formats</h1>
              <p className="text-[11px] text-slate-500 font-mono">Compression ≤ 2.3 MB</p>
            </div>
          </div>
        </div>
        <button
          data-testid="launch-declinations-button"
          onClick={launch}
          disabled={isRunning}
          className="h-11 px-6 bg-[#f58207] text-white font-bold text-sm rounded-lg hover:bg-[#c86b05] transition-colors flex items-center gap-2 shadow-[0_4px_14px_0_rgba(245,130,7,0.39)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRunning ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération... {progress}</>
            : <><Play className="w-4 h-4" /> Lancer les déclinaisons</>}
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.4fr] gap-6 p-6 flex-1">
        {/* LEFT — selection */}
        <div className="space-y-6 overflow-y-auto">
          {/* Creatives */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[11px] font-bold tracking-[0.2em] text-slate-900 uppercase">
                1 · Créas à décliner
                <span className="ml-2 text-slate-500 font-mono normal-case font-medium tracking-normal">
                {selectedImages.size}/{currentJob.images.length + 1}
                </span>
              </h2>
              <button
                data-testid="toggle-all-images-button"
                onClick={toggleAllImages}
                className="text-xs font-medium text-[#f58207] hover:underline"
              >
                {selectedImages.size === currentJob.images.length + 1 ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {[originalImage, ...currentJob.images].map(img => {
                const checked = selectedImages.has(img.index);
                return (
                  <div
                    key={img.index}
                    role="button"
                    tabIndex={0}
                    data-testid={`select-image-${img.index}`}
                    onClick={() => toggleImage(img.index)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") toggleImage(img.index);
                    }}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      checked ? "border-[#f58207] ring-2 ring-[#f58207]/30" : "border-slate-200 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={`${BACKEND_URL}${img.url}`} alt={img.index === 0 ? "Image originale" : `v${img.index}`} className="w-full h-full object-cover" />
                    <span className="absolute top-1 left-1 bg-white/90 text-[10px] font-bold px-1.5 py-0.5 rounded text-slate-900">
                      {img.index === 0 ? "Originale" : `#${img.index}`}
                    </span>
                    {img.index === 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingImage(img);
                        }}
                        className="absolute bottom-1 left-1 bg-[#f58207] text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md flex items-center gap-1 hover:bg-[#c86b05] transition-colors"
                      >
                        <Wand2 className="w-3 h-3" />
                        Modifier l'originale
                      </button>
                    )}
                    {checked && (
                      <span className="absolute top-1 right-1 bg-[#f58207] text-white w-5 h-5 rounded-full flex items-center justify-center shadow-sm">
                        <Check className="w-3.5 h-3.5 text-white stroke-[3.5]" />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Formats */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[11px] font-bold tracking-[0.2em] text-slate-900 uppercase">
                2 · Formats
                <span className="ml-2 text-slate-500 font-mono normal-case font-medium tracking-normal">
                  {totalSelected}/{totalAvailable}
                </span>
              </h2>
              <button
                data-testid="toggle-all-formats-button"
                onClick={toggleSelectAll}
                className="text-xs font-medium text-[#f58207] hover:underline"
              >
                {totalSelected === totalAvailable ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
            </div>

            <p className="text-[11px] text-slate-500 mb-3 italic">
              Compression max 2.3 MB (qualité maximale) · Survolez un format pour prévisualiser le recadrage
            </p>

            <div className="space-y-4">
              {FORMAT_CATALOG.map(cat => {
                const catKeys = cat.items.map(it => formatKey(cat.category, it));
                const allChecked = catKeys.every(k => selectedFormats.has(k));
                const someChecked = catKeys.some(k => selectedFormats.has(k));
                return (
                  <div key={cat.category} className="border border-slate-100 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`category-checkbox-${cat.category}`}
                          checked={allChecked}
                          ref={el => { if (el) el.indeterminate = !allChecked && someChecked; }}
                          onChange={() => toggleAllInCategory(cat)}
                          className="w-4 h-4 accent-[#f58207]"
                        />
                        <span className="text-xs font-bold tracking-wider text-slate-900 uppercase">{cat.label}</span>
                      </label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {cat.items.map(it => {
                        const k = formatKey(cat.category, it);
                        const checked = selectedFormats.has(k);
                        return (
                          <button
                            key={k}
                            data-testid={`format-chip-${cat.category}-${it.name.replace(/\s+/g, '_')}`}
                            onClick={() => toggleFormat(k)}
                            onMouseEnter={(e) => showPreview(e, it)}
                            onMouseLeave={hidePreview}
                            className={`text-[11px] font-mono px-2.5 py-1.5 rounded-md border transition-all ${
                              checked
                                ? "bg-[#f58207] text-white border-[#f58207] shadow-sm"
                                : "bg-slate-50 text-slate-700 border-slate-200 hover:border-[#f58207]/40"
                            }`}
                          >
                            {it.name} <span className="opacity-70">({it.width}×{it.height})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Custom formats */}
              <div className="border border-slate-100 rounded-lg p-3 bg-slate-50/60">
                <span className="text-xs font-bold tracking-wider text-slate-900 uppercase block mb-3">Format personnalisé</span>
                <div
                  className="flex flex-wrap items-end gap-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomFormat();
                    }
                  }}
                >
                  <input
                    data-testid="custom-format-name-input"
                    value={customName}
                    onChange={e => setCustomName(e.target.value)}
                    placeholder="Nom (opt.)"
                    className="h-9 px-3 text-xs bg-white border border-slate-200 rounded-md w-32 outline-none focus:border-[#f58207]"
                  />
                  <input
                    data-testid="custom-format-width-input"
                    type="number"
                    value={customWidth}
                    onChange={e => setCustomWidth(e.target.value)}
                    placeholder="Largeur"
                    className="h-9 px-3 text-xs bg-white border border-slate-200 rounded-md w-24 outline-none focus:border-[#f58207] font-mono"
                  />
                  <span className="text-slate-400 font-mono">×</span>
                  <input
                    data-testid="custom-format-height-input"
                    type="number"
                    value={customHeight}
                    onChange={e => setCustomHeight(e.target.value)}
                    placeholder="Hauteur"
                    className="h-9 px-3 text-xs bg-white border border-slate-200 rounded-md w-24 outline-none focus:border-[#f58207] font-mono"
                  />
                  <button
                    type="button"
                    data-testid="add-custom-format-button"
                    onClick={addCustomFormat}
                    className="h-9 px-3 bg-slate-900 text-white text-xs font-bold rounded-md hover:bg-black transition-colors flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" /> Ajouter
                  </button>
                </div>
                {customFormats.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {customFormats.map(it => {
                      const k = formatKey("CUSTOM", it);
                      const checked = selectedFormats.has(k);
                      return (
                        <div key={k} className="flex items-center gap-1.5">
                          <button
                            type="button"
                            data-testid={`format-chip-CUSTOM-${it.name.replace(/\s+/g, '_')}`}
                            onClick={() => toggleFormat(k)}
                            onMouseEnter={(e) => showPreview(e, it)}
                            onMouseLeave={hidePreview}
                            className={`text-[11px] font-mono px-2.5 py-1.5 rounded-md border transition-all ${
                              checked ? "bg-[#f58207] text-white border-[#f58207] shadow-sm"
                                : "bg-white text-slate-700 border-slate-200"
                            }`}
                          >
                            {it.name} <span className="opacity-70">({it.width}×{it.height})</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => removeCustomFormat(it)}
                            className="text-slate-400 hover:text-red-500 text-xs"
                            title="Retirer"
                          >×</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* RIGHT — terminal + results */}
        <div className="flex flex-col gap-6 min-h-0">
          <TerminalPanel
            title="Journal d'activité — Déclinaisons IA"
            testId="declinations-activity-log"
            badgeTestId="declinations-status-badge"
            emptyText="$ En attente du lancement... Sélectionnez vos créas et vos formats."
            logs={decl?.logs || []}
            status={decl?.status || (launching ? "pending" : "idle")}
            footer={downloadReady ? (
              <button
                data-testid="declinations-zip-download-line"
                onClick={downloadZip}
                className="mt-2 flex items-center gap-2 text-[#00FF41] hover:text-white transition-colors font-mono text-xs underline underline-offset-2"
              >
                <Download className="w-3.5 h-3.5" /> $ telecharger en zip les images (organisé par dossiers)
              </button>
            ) : null}
          />

          <div className="bg-white rounded-xl border border-slate-200 p-5 flex-1 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[11px] font-bold tracking-[0.2em] text-slate-900 uppercase">
                Résultats
                {decl && (
                  <span className="ml-2 text-slate-500 font-mono normal-case font-medium tracking-normal">
                    {decl.results?.length || 0}/{decl.total}
                  </span>
                )}
              </h2>
              {downloadReady && (
                <div className="flex items-center gap-2">
                  <button
                    data-testid="logo-swap-button"
                    onClick={() => setShowLogoSwap(true)}
                    className="h-9 px-4 bg-[#f58207] text-white text-xs font-bold rounded-md hover:bg-[#c86b05] transition-colors flex items-center gap-2"
                  >
                    <Stamp className="w-3.5 h-3.5" /> Remplacer le logo d'abord
                  </button>
                  <button
                    data-testid="download-declinations-zip-button"
                    onClick={downloadZip}
                    className="h-9 px-4 bg-slate-900 text-white text-xs font-bold rounded-md hover:bg-black transition-colors flex items-center gap-2"
                  >
                    <Download className="w-3.5 h-3.5" /> Télécharger directement
                  </button>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {(!decl || (decl.results?.length || 0) === 0) ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-lg">
                  Les déclinaisons apparaîtront ici, classées par catégorie.
                </div>
              ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="declinations-grid">
                  {decl.results.map((r, i) => (
                    <div
                      key={i}
                      data-testid={`declination-result-${i}`}
                      className="group relative block w-full text-left rounded-lg overflow-hidden border border-slate-200 hover:border-[#f58207] hover:shadow-md transition-all"
                    >
                      <button
                        type="button"
                        onClick={() => setModalImage({
                          url: r.url,
                          label: `${r.format_name} · ${r.width}×${r.height}`,
                          filename: r.filename,
                        })}
                        className="block w-full text-left"
                      >
                        <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
                          <img src={`${BACKEND_URL}${r.url}`} alt={r.format_name} className="max-w-full max-h-full object-contain" />
                        </div>
                        <div className="p-2 bg-white">
                          <p className="text-[10px] font-bold tracking-wider text-slate-500 uppercase truncate">{r.category.replace(/_/g, " ")}</p>
                          <p className="text-[11px] font-mono text-slate-900 truncate">{r.format_name} · {r.width}×{r.height}</p>
                          <p className="text-[10px] text-slate-400 font-mono">#{r.source_index} · {r.size_kb} KB</p>
                        </div>
                      </button>
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
                      <button
                        type="button"
                        aria-label="Modifier l'image"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingImage({
                            index: i,
                            url: r.url,
                            filename: r.filename,
                            label: `${r.format_name} · ${r.width}×${r.height}`,
                            isOriginal: (r.source_index ?? i) === 0,
                            declinationId: decl.id,
                            declinationResultIndex: i,
                            sourceIndex: r.source_index ?? i,
                            category: r.category,
                            format_name: r.format_name,
                            width: r.width,
                            height: r.height,
                          });
                        }}
                        className="h-9 px-3 rounded-full bg-white/95 text-slate-900 shadow-lg flex items-center gap-2 border border-slate-200 opacity-90 group-hover:opacity-100 group-focus-within:opacity-100 hover:scale-105 transition-all"
                      >
                          <Wand2 className="w-4 h-4 text-[#f58207]" />
                          <span className="text-[11px] font-bold">Modifier l'image</span>
                        </button>
                        <button
                          type="button"
                          aria-label="Relancer cette déclinaison"
                          onClick={(e) => {
                            e.stopPropagation();
                            rerunDeclination(i);
                          }}
                          disabled={rerunningIndex !== null}
                          className="disabled:cursor-not-allowed"
                        >
                          <span className="w-12 h-12 rounded-full bg-white/95 text-slate-900 shadow-lg flex items-center justify-center border border-slate-200 opacity-80 group-hover:opacity-100 group-focus-within:opacity-100 hover:scale-105 transition-all">
                            {rerunningIndex === i ? <Loader2 className="w-5 h-5 animate-spin text-[#f58207]" /> : <RefreshCw className="w-5 h-5" />}
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
